import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { animeCategories, animeList } from '@/mocks/animeData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'guoman');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [activeCategory]);

  const currentCat = animeCategories.find((c) => c.slug === activeCategory);
  const filteredAnime = animeList.filter((a) => a.category === activeCategory);
  const otherAnime = animeList.filter((a) => a.category !== activeCategory);

  const currentCatIndex = animeCategories.findIndex((c) => c.slug === activeCategory);

  const catName = currentCat?.name || '分类';
  const catDesc = currentCat?.description || '';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${catName} - 91动漫`,
    description: catDesc,
    url: `https://beyondtoon.github.io/category/${activeCategory}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredAnime.map((a, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'CreativeWork',
          name: a.title,
          url: `https://beyondtoon.github.io/article/${a.id}`,
        },
      })),
    },
  };

  return (
    <Layout>
      <SEOHead
        title={`${catName}动漫在线观看 - 91动漫`}
        description={`91动漫${catName}专区：${catDesc}。海量${catName}资源，高清免费在线播放，每日更新。`}
        keywords={`${catName},${catName}动漫,${catName}在线观看,91动漫`}
        canonicalPath={`/category/${activeCategory}`}
        schema={schema}
      />

      <div className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '分类浏览' },
              { label: catName },
            ]}
          />

          <div className="mt-6 mb-10">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              {catName}动漫
            </h1>
            <p className="text-sm text-foreground-500 mt-2">{catDesc}</p>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
            {animeCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${activeCategory === cat.slug ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-primary-50 hover:text-primary-500'}`}
              >
                <i className={`${cat.icon} text-sm w-4 h-4 flex items-center justify-center`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredAnime.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {filteredAnime.map((anime, index) => (
                  <Link
                    key={anime.id}
                    to={`/article/${anime.id}`}
                    className="group cursor-pointer"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
                      <img
                        src={anime.cover}
                        alt={anime.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading={index > 4 ? 'lazy' : undefined}
                      />
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-primary-500 text-background-50 text-xs font-medium whitespace-nowrap">
                        {anime.rating}
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                        <span className="text-white text-xs whitespace-nowrap">{anime.episodes}</span>
                      </div>
                    </div>
                    <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1">
                      {anime.title}
                    </h3>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      {anime.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="text-xs text-foreground-400 whitespace-nowrap">{tag}</span>
                      ))}
                    </div>
                  </Link>
                ))}
              </div>

              {otherAnime.length > 0 && (
                <div className="mt-14">
                  <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-6">
                    其他分类推荐
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                    {otherAnime.map((anime, index) => (
                      <Link
                        key={anime.id}
                        to={`/article/${anime.id}`}
                        className="group cursor-pointer"
                      >
                        <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3">
                          <img
                            src={anime.cover}
                            alt={anime.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                          <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-background-600 text-background-50 text-xs font-medium whitespace-nowrap">
                            {anime.rating}
                          </div>
                        </div>
                        <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1">
                          {anime.title}
                        </h3>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-emotion-sad-line text-3xl"></i>
              </div>
              <p className="text-foreground-500">该分类暂无动漫资源，请浏览其他分类</p>
              <Link
                to="/category/riman"
                className="inline-flex items-center gap-2 mt-4 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                前往日漫专区
              </Link>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}